/**
 * /sequences — list. Cycle 13.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface SequenceRow {
  id: string;
  name: string;
  description: string | null;
  igAccountId: string;
  defaultMessageTag: string | null;
  stepCount: number;
  activeSubs: number;
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute("/_app/sequences/")({
  component: SequencesList,
});

function SequencesList() {
  const { activeIgAccount } = useAuth();
  const [rows, setRows] = useState<SequenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeIgAccount) return;
    void load();
  }, [activeIgAccount?.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ ig_account_id: activeIgAccount!.id });
      const r = await api.get<{ sequences: SequenceRow[] }>(`/sequences?${params}`);
      setRows(r.data.sequences);
    } catch {
      setError("Failed to load sequences");
    } finally {
      setLoading(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this sequence?")) return;
    try {
      await api.delete(`/sequences/${id}`);
      setRows((cur) => cur.filter((s) => s.id !== id));
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? "Failed to delete");
    }
  }

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Sequences</h1>
        <Link to="/sequences/new" className="brut-button">+ New sequence</Link>
      </header>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        A drip series of messages spaced over time. Subscribe contacts via a flow node.
      </p>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>{error}</div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No sequences yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Sequences are timed multi-message drips — welcome series,
            re-engagement, post-purchase. Create one above ↑ then have
            a flow's <code>subscribe_sequence</code> node enroll contacts.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {rows.map((s) => (
            <li
              key={s.id}
              className="brut-card"
              style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}
            >
              <div style={{ minWidth: 0 }}>
                <Link to="/sequences/$sequenceId" params={{ sequenceId: s.id }} style={{ fontWeight: 800, fontSize: 16, textDecoration: "none" }}>
                  {s.name}
                </Link>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
                  {s.stepCount} step{s.stepCount === 1 ? "" : "s"} · {s.activeSubs} active subscriber{s.activeSubs === 1 ? "" : "s"}
                </div>
                {s.description ? (
                  <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 2 }}>{s.description}</div>
                ) : null}
              </div>
              <button
                onClick={() => void remove(s.id)}
                className="brut-button is-danger"
                style={{ fontSize: 13, padding: "6px 10px" }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
